// src/pages/Dashboard.tsx

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  useTheme,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Paper,
  LinearProgress,
  alpha,
  useMediaQuery,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import {
  ShoppingCart,
  Store,
  Inventory,
  LocationOn,
  Refresh,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  Timeline,
  InfoOutlined,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import StoreIcon from '@mui/icons-material/Store';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HomeIcon from '@mui/icons-material/Home';
import { motion } from 'framer-motion';

import { fetchTopProducts } from '../api/analyticsApi';
import {
  fetchKPIs,
  fetchSalesTrend,
  fetchInventoryDistribution,
  fetchSalesByLocation,
  fetchCategories,
  fetchLocations,
} from '../api/analyticsApi';

// Modern futuristic color palette
const CHART_COLORS = {
  primary: '#7C3AED',
  secondary: '#10B981',
  accent: '#F59E0B',
  info: '#3B82F6',
  warning: '#EF4444',
  gray: '#6B7280',
  chart: ['#8B5CF6', '#10B981', '#F59E0B', '#3B82F6', '#EC4899', '#6366F1'],
};

// Glass morphism styles
const glassStyle = {
  backgroundColor: 'background.paper',
  backdropFilter: 'blur(10px)',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 3,
};

// Define types
type KPI = {
  title: string;
  value: string | number;
  icon: JSX.Element;
  color: string;
};

type SalesData = {
  month: string;
  sales: number;
  goal?: number;
};

type StockData = {
  category: string;
  in_store: number;
  online: number;
};

type SalesByLocation = {
  location: string;
  value: number;
};

type Category = {
  id: number;
  name: string;
};

type Location = {
  id: number;
  name: string;
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    dataKey?: string;
    payload?: {
      fill?: string;
    };
  }>;
  label?: string;
}

type TopProduct = {
  name: string;
  fullName?: string;
  units_sold: number;
  revenue: number;
};


// Compact tooltip
const CustomTooltip: React.FC<TooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        elevation={8}
        sx={{
          ...glassStyle,
          p: 1.5,
          borderRadius: 2,
          minWidth: 120,
        }}
      >
        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: entry.color || entry.payload?.fill }} />
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {entry.name}: {entry.dataKey?.includes('sales') || entry.dataKey?.includes('revenue')
                ? `$${entry.value.toLocaleString()}`
                : entry.value.toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }
  return null;
};

// Compact KPI Card - with special handling for product names
// Compact KPI Card - with better mobile handling
const CompactKPICard: React.FC<{ kpi: KPI; index: number }> = ({ kpi, index }) => {
  const isProductCard = kpi.title.includes('Top Product');
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Card
        sx={{
          borderRadius: 2.5,
          background: `linear-gradient(135deg, ${alpha(kpi.color, 0.08)} 0%, ${alpha(kpi.color, 0.02)} 100%)`,
          backdropFilter: 'blur(8px)',
          border: `1px solid ${alpha(kpi.color, 0.15)}`,
          transition: 'all 0.2s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${alpha(kpi.color, 0.2)}` },
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                bgcolor: alpha(kpi.color, 0.15),
                color: kpi.color,
                width: isMobile ? 36 : 44,
                height: isMobile ? 36 : 44,
                borderRadius: 2,
              }}
            >
              {React.cloneElement(kpi.icon, { sx: { fontSize: isMobile ? 20 : 24 } })}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    // Remove whiteSpace: 'nowrap' on mobile, keep on desktop
                    whiteSpace: isMobile ? 'normal' : 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                  }}
                >
                  {kpi.title}
                </Typography>
                {isProductCard && (
                  <MuiTooltip title="Current top performing product" arrow>
                    <InfoOutlined sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.5 }} />
                  </MuiTooltip>
                )}
              </Box>
              <Typography
                variant={isProductCard ? "body2" : "h6"}
                fontWeight={isProductCard ? 500 : 700}
                sx={{
                  whiteSpace: isMobile ? 'normal' : 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: isProductCard ? '0.85rem' : (isMobile ? '1rem' : undefined),
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                  mt: 0.5,
                }}
              >
                {kpi.value}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Compact Chart Card
const CompactChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card sx={{ borderRadius: 2.5, ...glassStyle }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Timeline sx={{ fontSize: 18, color: CHART_COLORS.primary }} />
          {title}
        </Typography>
        <IconButton size="small" sx={{ color: 'text.secondary' }}>
          <MoreVert sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      {children}
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [kpis, setKpis] = useState<KPI[]>([]);
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [stockData, setStockData] = useState<StockData[]>([]);
  const [salesByLocation, setSalesByLocation] = useState<SalesByLocation[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [monthOverMonthChange, setMonthOverMonthChange] = useState<number>(0);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);


  const getChartHeight = () => {
    if (isMobile) return 200;
    if (isTablet) return 250;
    return 280;
  };

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    try {
      const [
        kpisRes,
        salesTrendRes,
        inventoryRes,
        salesLocationRes,
        categoriesRes,
        locationsRes,
        topProductsRes,
      ] = await Promise.all([
        fetchKPIs(),
        fetchSalesTrend(),
        fetchInventoryDistribution(),
        fetchSalesByLocation(),
        fetchCategories(),
        fetchLocations(),
        fetchTopProducts(5),
      ]);

      // Use REAL data only
      setKpis(kpisRes);

      // Process top products - truncate long names for display
      setTopProducts(
        topProductsRes.map((r: any) => ({
          name: r.name?.length > 20 ? r.name.substring(0, 18) + '...' : r.name || 'Unknown',
          fullName: r.name, // Keep full name for tooltip
          units_sold: Number(r.units_sold || 0),
          revenue: Number(r.revenue || 0),
        }))
      );

      setSalesData(salesTrendRes);
      setStockData(inventoryRes);
      setSalesByLocation(
        salesLocationRes.map((item: { location: string; value: string | number }) => ({
          location: item.location,
          value: Number(item.value),
        }))
      );
      setCategories(categoriesRes);
      setLocations(locationsRes);
      setLastUpdated(new Date());

      // Calculate REAL month-over-month change
      if (salesTrendRes.length > 1) {
        const currentMonth = salesTrendRes[salesTrendRes.length - 1].sales;
        const prevMonth = salesTrendRes[salesTrendRes.length - 2].sales;
        if (prevMonth !== 0) {
          const change = ((currentMonth - prevMonth) / prevMonth) * 100;
          setMonthOverMonthChange(parseFloat(change.toFixed(1)));
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      if (showRefresh) setTimeout(() => setIsRefreshing(false), 1000);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 3600000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => fetchData(true);

  const generateColors = (items: Array<Category | Location>) => {
    return items.reduce((acc, item, index) => ({
      ...acc,
      [item.name]: CHART_COLORS.chart[index % CHART_COLORS.chart.length],
    }), {} as Record<string, string>);
  };

  const locationColors = generateColors(locations);
  const totalSales = salesByLocation.reduce((sum, entry) => sum + Number(entry.value || 0), 0);

  // Improved Y-axis formatting for better readability with larger numbers
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`; // Removed decimal for cleaner look
    return `$${value}`;
  };

  // Calculate appropriate Y-axis domain
  const getYAxisDomain = () => {
    if (salesData.length === 0) return [0, 100];
    const maxSales = Math.max(...salesData.map(d => d.sales));
    const buffer = maxSales * 0.1; // Add 10% padding
    return [0, Math.ceil((maxSales + buffer) / 1000) * 1000]; // Round to nearest thousand
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 1.5, sm: 2, md: 2.5 },
      maxWidth: '100%',
      overflowX: 'hidden',
      bgcolor: 'background.default',
      minHeight: '100vh',
    }}>
      {/* Compact Header */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        <Paper
          sx={{
            ...glassStyle,
            borderRadius: 2.5,
            p: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 2.5 },
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 0 }
          }}>
            <Box>
              <Typography variant="h6" fontWeight="700">
                Dashboard
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  label={formatDistanceToNow(lastUpdated, { addSuffix: true })}
                  sx={{ height: 24, fontSize: '0.7rem' }}
                />
                {monthOverMonthChange !== 0 && (
                  <Chip
                    size="small"
                    icon={monthOverMonthChange > 0 ? <ArrowUpward sx={{ fontSize: 12 }} /> : <ArrowDownward sx={{ fontSize: 12 }} />}
                    label={`${monthOverMonthChange > 0 ? '+' : ''}${monthOverMonthChange}% vs last month`}
                    sx={{
                      height: 24,
                      fontSize: '0.7rem',
                      bgcolor: alpha(monthOverMonthChange > 0 ? CHART_COLORS.secondary : CHART_COLORS.warning, 0.1),
                      color: monthOverMonthChange > 0 ? CHART_COLORS.secondary : CHART_COLORS.warning,
                    }}
                  />
                )}
              </Stack>
            </Box>
            <IconButton onClick={handleRefresh} size="small" sx={{ ...glassStyle, alignSelf: { xs: 'flex-end', sm: 'center' } }}>
              <Refresh sx={{ fontSize: 18, animation: isRefreshing ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </Box>
        </Paper>
      </motion.div>

      {/* KPI Grid */}
      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        {kpis.map((kpi, index) => {
          let icon: JSX.Element = <InventoryIcon />;
          let color = CHART_COLORS.info;

          // Icon mapping based on title
          if (kpi.title.includes('Online Sales')) {
            icon = <OnlinePredictionIcon />;
            color = CHART_COLORS.primary;
          } else if (kpi.title.includes('In-Store Sales')) {
            icon = <StoreIcon />;
            color = CHART_COLORS.secondary;
          } else if (kpi.title.includes('Active Online')) {
            icon = <ShoppingCartIcon />;
            color = CHART_COLORS.accent;
          } else if (kpi.title.includes('Active In-Store')) {
            icon = <InventoryIcon />;
            color = CHART_COLORS.info;
          } else if (kpi.title === 'Locations') {
            icon = <LocationOnIcon />;
            color = CHART_COLORS.gray;
          } else if (kpi.title === 'Avg. Order Value') {
            icon = <MonetizationOnIcon />;
            color = CHART_COLORS.secondary;
          } else if (kpi.title === 'Top Product (Units)') {
            icon = <StarBorderIcon />;
            color = CHART_COLORS.primary;
          } else if (kpi.title === 'Top Product (Revenue)') {
            icon = <MonetizationOnIcon />;
            color = CHART_COLORS.secondary;
          } else if (kpi.title === 'Paid Orders Today') {
            icon = <ShoppingCartIcon />;
            color = CHART_COLORS.info;
          } else if (kpi.title === 'Shipping Orders') {
            icon = <LocalShippingIcon />;
            color = CHART_COLORS.gray;
          } else if (kpi.title === 'Local Delivery Orders') {
            icon = <HomeIcon />;
            color = CHART_COLORS.accent;
          }

          return (
            <Grid item xs={6} sm={6} md={3} key={index}>
              <CompactKPICard kpi={{ ...kpi, icon, color }} index={index} />
            </Grid>
          );
        })}
      </Grid>

      {/* Charts Grid */}
      <Grid container spacing={1.5}>
        {/* Sales Trend Chart - with improved Y-axis */}
        <Grid item xs={12} md={8}>
          <CompactChartCard title="Sales Trend">
            <ResponsiveContainer width="100%" height={getChartHeight()}>
              <AreaChart data={salesData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10 }}
                  interval={isMobile ? 1 : 0}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatYAxis}
                  tick={{ fontSize: 10 }}
                  width={40}
                  domain={getYAxisDomain()}
                  allowDataOverflow={false}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="sales"
                  stroke={CHART_COLORS.primary}
                  strokeWidth={2}
                  fill="url(#salesGradient)"
                />
                {salesData[0]?.goal && (
                  <Line
                    type="monotone"
                    dataKey="goal"
                    stroke={CHART_COLORS.gray}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    dot={false}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </CompactChartCard>
        </Grid>

        {/* Inventory Distribution */}
        {/* Inventory Distribution */}
        <Grid item xs={12} md={4}>
          <CompactChartCard title="Inventory Distribution">
            <ResponsiveContainer width="100%" height={getChartHeight()}>
              <BarChart data={stockData} layout="vertical" margin={{ left: 15, right: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.divider, 0.3)} />
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10 }}
                  domain={[(dataMin: number) => Math.min(dataMin, 0), 'dataMax']}
                  tickFormatter={(value: number) => value.toString()}
                />
                <YAxis
                  type="category"
                  dataKey="category"
                  width={60}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10 }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="online"
                  name="Online"
                  fill={CHART_COLORS.primary}
                  radius={[0, 4, 4, 0]}
                  barSize={16}
                />
                <Bar
                  dataKey="in_store"
                  name="In-Store"
                  fill={CHART_COLORS.secondary}
                  radius={[0, 4, 4, 0]}
                  barSize={16}
                />
              </BarChart>
            </ResponsiveContainer>
          </CompactChartCard>
        </Grid>

        {/* Top Products - with tooltips for full names */}
        <Grid item xs={12} md={6}>
          <CompactChartCard title="Top Products">
            <ResponsiveContainer width="100%" height={getChartHeight()}>
              <BarChart
                data={topProducts}
                layout="vertical"
                margin={{ left: 20, right: 10, top: 5, bottom: 5 }} // Reduced left margin
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.divider, 0.3)} />
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={isMobile ? 70 : 90} // Increased width to show more of the product name
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => {
                    // Show more of the product name on the axis
                    return value.length > (isMobile ? 8 : 12) ? value.substring(0, isMobile ? 8 : 12) + '...' : value;
                  }}
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                  formatter={(value: any, name: any) => {
                    if (name === 'units_sold') {
                      return [value.toLocaleString(), 'Units Sold'];
                    }
                    return [value, name];
                  }}
                  labelFormatter={(label) => {
                    const product = topProducts.find(p => p.name === label);
                    return product?.fullName || label;
                  }}
                />
                <Bar
                  dataKey="units_sold"
                  name="Units Sold"
                  fill={CHART_COLORS.accent}
                  radius={[0, 4, 4, 0]}
                  barSize={16} // Slightly larger bars
                />
              </BarChart>
            </ResponsiveContainer>
          </CompactChartCard>
        </Grid>

        {/* Sales by Location */}
        <Grid item xs={12} md={6}>
          <CompactChartCard title="Revenue by Location">
            <ResponsiveContainer width="100%" height={getChartHeight()}>
              <PieChart>
                <Pie
                  data={salesByLocation}
                  dataKey="value"
                  nameKey="location"
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 35 : 45}
                  outerRadius={isMobile ? 55 : 70}
                  paddingAngle={2}
                >
                  {salesByLocation.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={locationColors[entry.location] || CHART_COLORS.chart[index % CHART_COLORS.chart.length]} />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomTooltip />} />
                {totalSales > 0 && (
                  <>
                    <text x="50%" y="45%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: isMobile ? '0.8rem' : '1rem', fontWeight: 600 }}>
                      ${(totalSales / 1000).toFixed(0)}k
                    </text>
                    <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '0.6rem', fill: theme.palette.text.secondary }}>
                      Total
                    </text>
                  </>
                )}
              </PieChart>
            </ResponsiveContainer>
          </CompactChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;